import compact from 'lodash/compact';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StopPlaceOrganisationFieldsFragment,
  useGetOrganisationsQuery,
} from '../../../../../../generated/graphql';
import { SimpleButton } from '../../../../../common/Buttons';
import { InputField } from '../../../../../common/Inputs';
import { Column, Visible } from '../../../../../common/LayoutComponents';
import { OrganisationDetailsModal } from '../../../../Stops/StopDetails/Maintenance/OrganisationDetailsModal';
import {
  CREATE_NEW_ORGANISATION_OPTION,
  ChooseOrganisationDropdown,
} from '../../../../Stops/StopDetails/Maintenance/OrganisationDropdown';
import { TerminalOwnerFormState } from './terminalOwnerSchema';

const testIds = {
  ownerDropdown: 'OwnerOrganizationFields::ownerDropdown',
  editOrganisationButton: 'OwnerOrganizationFields::editOrganisationButton',
  phone: 'OwnerOrganizationFields::phone',
  email: 'OwnerOrganizationFields::email',
};

type MaintainerFormFieldsProps = {
  readonly testId: string;
};

export const OwnerOrganizationFields: FC<MaintainerFormFieldsProps> = ({
  testId,
}) => {
  const { t } = useTranslation();

  const { data, refetch } = useGetOrganisationsQuery();
  const organisations = useMemo(
    () => compact(data?.stop_registry?.organisation),
    [data],
  );

  const { setValue, resetField, watch } =
    useFormContext<TerminalOwnerFormState>();

  const selectedOwnerRef = watch('ownerRef') ?? null;
  const selectedOwner =
    organisations.find((o) => o?.id === selectedOwnerRef) ?? null;
  const [previousValue, setPreviousValue] = useState<string | null>(
    selectedOwnerRef,
  );

  const [isEditingOrganisation, setIsEditingOrganisation] = useState(false);
  const [editedOrganisation, setEditedOrganisation] =
    useState<StopPlaceOrganisationFieldsFragment | null>(null);

  const onEditOrganisation = useCallback(
    (org: StopPlaceOrganisationFieldsFragment | null) => {
      setIsEditingOrganisation(true);
      setEditedOrganisation(org);
    },
    [],
  );

  const onStopEditingOrganisation = useCallback(() => {
    setIsEditingOrganisation(false);
    setEditedOrganisation(null);
  }, []);

  const onOrganisationUpdated = useCallback(
    (org: StopPlaceOrganisationFieldsFragment) => {
      const organisationId = org.id;
      onStopEditingOrganisation();
      // Select the newly created organisation as owner (already selected if editing).
      if (organisationId) {
        setValue('ownerRef', organisationId);
      }
      refetch();
    },
    [onStopEditingOrganisation, setValue, refetch],
  );

  useEffect(() => {
    if (selectedOwnerRef === CREATE_NEW_ORGANISATION_OPTION) {
      onEditOrganisation(null);
      // "Reset" to previous value immediately,
      // to have a sensible value selected in case user cancels adding new organisation.
      setValue('ownerRef', previousValue);
    } else if (previousValue !== selectedOwnerRef) {
      setPreviousValue(selectedOwnerRef);
    }
  }, [
    previousValue,
    selectedOwnerRef,
    onEditOrganisation,
    resetField,
    setValue,
  ]);

  return (
    <>
      <Column data-testid={testId}>
        <InputField<TerminalOwnerFormState>
          translationPrefix="terminalDetails.owner"
          customTitlePath="terminalDetails.owner.owner"
          fieldPath="ownerRef"
          testId={testIds.ownerDropdown}

          inputElementRenderer={(props) => (
            <ChooseOrganisationDropdown {...props} />
          )}
        />

        <div className="my-2 text-sm">
          <div data-testid={testIds.phone}>
            {selectedOwner?.privateContactDetails?.phone ?? ''}
          </div>
          <div data-testid={testIds.email}>
            {selectedOwner?.privateContactDetails?.email ?? ''}
          </div>
        </div>

        <div>
          <Visible visible={!!selectedOwner}>
            <SimpleButton
              shape="slim"
              className="px-8"
              inverted
              onClick={() => onEditOrganisation(selectedOwner)}
              testId={testIds.editOrganisationButton}
            >
              {t(($) => $.edit)}
            </SimpleButton>
          </Visible>
        </div>
      </Column>
      <OrganisationDetailsModal
        isOpen={isEditingOrganisation}
        organisation={editedOrganisation}
        onClose={onStopEditingOrganisation}
        onSubmit={onOrganisationUpdated}
      />
    </>
  );
};
